import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classNames } from '@ux/component-utilities';
import Button from '@ux/button';
import XIcon from '@ux/icon/x';
import { ICONS } from './icons.js';


const UX_ALERT_CLASSNAME = 'ux-alert';

const TYPE_TO_EMPHASIS_MAP = {
  danger: 'critical',
  offer: 'highlight',
  note: 'neutral',
  c3: 'internal'
};

/**
 * Alert notifications component.
 *
 * Alerts may be dissmissible, or not have a close button.
 *
 * @displayName Alert
 * @class Alert
 * @public
 */
export default class Alert extends Component {
  constructor() {
    super(...arguments);
    this._warn();
    this.state = { open: true };
  }

  _warn() {
    const {
      message, // eslint-disable-line react/prop-types
      header, // eslint-disable-line react/prop-types
      type // eslint-disable-line react/prop-types
    } = this.props;

    if (process.env.NODE_ENV === 'production') return;

    const mappedEmphasis = TYPE_TO_EMPHASIS_MAP[type];
    if (typeof mappedEmphasis !== 'undefined') {
      console.warn([
        `Warning: Alert prop 'type' was deprecated and replaced by 'emphasis' in v2002 and will be removed in v2100. `,
        `The legacy type='${type}' best maps to emphasis='${mappedEmphasis}'. See http://x.co/6nzLv`
      ].join(''));
    }
    if (message || header) {
      console.warn([
        `Warning: The 'type', 'message', and 'header' props were deprecated in v2002 and will be removed in v2100. `,
        `The alert component is now operating in legacy mode and should be updated. See http://x.co/6nzLv`
      ].join(''));
    }
  }

  close() {
    this.setState({ open: false });
    this.props.onClose && this.props.onClose();
  }

  legacyChildren() {
    const {
      children,
      message, // eslint-disable-line react/prop-types
      header, // eslint-disable-line react/prop-types
      type // eslint-disable-line react/prop-types
    } = this.props;

    // If legacy props are not used, then this component is being used correctly
    if (!message && !header && !type) return children;

    return <>
      {header && <><strong className='headline-primary' role='heading' aria-level='1'>{header}</strong>{' '}</>}
      {message && <>{message}{' '}</>}
      {children}
    </>;
  }

  /**
   * Render the alert component.
   *
   * @returns {ReactElement} Alert box.
   * @public
   */
  render() {
    const {
      button,
      className,
      dismissible,
      emphasis,
      ariaLabels
    } = this.props;

    // @TODO Remove in v2100
    const {
      type // eslint-disable-line react/prop-types
    } = this.props;
    const mappedEmphasis = TYPE_TO_EMPHASIS_MAP[type] || emphasis;

    const classes = [UX_ALERT_CLASSNAME, `${UX_ALERT_CLASSNAME}-${mappedEmphasis}`, className].filter(Boolean);

    // Only allow data-* attributes to pass-through
    const passThroughProps = Object.keys(this.props).reduce((curr, propName) => {
      if (propName.startsWith('data-')) {
        curr[propName] = this.props[propName];
      }
      return curr;
    }, {});

    let closeButton;
    if (dismissible) {
      closeButton = <Button
        className={ `${UX_ALERT_CLASSNAME}-close-button` }
        design={ 'inline' }
        onClick={ this.close.bind(this) }
        icon={ <XIcon/> }
        aria-label={ ariaLabels.close }/>;
    }

    const Icon = ICONS[mappedEmphasis];
    let buttonComponent;
    if (button) {
      const { className: clsx } = button.props || {};
      const buttonClassName = classNames(`${UX_ALERT_CLASSNAME}-button`, clsx);
      buttonComponent = React.cloneElement(button, { className: buttonClassName, as: 'cta', design: 'inline' });
    }

    return (this.state.open &&
      <div className={ classes.join(' ') } role='alert' { ...passThroughProps }>
        { <Icon className={ `${UX_ALERT_CLASSNAME}-icon` }/> }
        <div className={ `${UX_ALERT_CLASSNAME}-content` }>
          {/* @TODO Replace `this.legacyChildren()` with just `children` in v2100 */}
          <div className={ `${UX_ALERT_CLASSNAME}-message` }>{ this.legacyChildren() }</div>
          { buttonComponent }
        </div>
        { closeButton }
      </div>
    );
  }
}
/**
 * The default properties.
 *
 * @type {Object}
 * @private
 */
Alert.defaultProps = {
  dismissible: true,
  ariaLabels: {
    close: 'Close'
  },
  emphasis: 'critical'
};
/**
 * Property types.
 *
 * @type {Object}
 * @private
 */
Alert.propTypes = {
  /** Adds a cta button */
  button: PropTypes.shape({ type: Button.type, props: PropTypes.object }),
  /** Additional alert detail content */
  children: PropTypes.node,
  /** Additional class name to target for positioning */
  className: PropTypes.string,
  /** Flag for whether the alert can be dismissed */
  dismissible: PropTypes.bool,
  /** Screenreader labels for sub-elements */
  ariaLabels: PropTypes.shape({
    /** Screen reader label for close button */
    close: PropTypes.string
  }),
  /** Describes the purpose of the alert */
  emphasis: PropTypes.oneOf(Object.keys(ICONS)),
  /** Callback function when alert is closed */
  onClose: PropTypes.func
};
